import { z } from 'zod';
import { MoneyStr } from './common.js';

// ─── Лента изменений балансов (/balances, блок «Журнал изменений») ──────────
//
// GET /api/v1/balance-changes?limit&before — курсорная пагинация по id
// (before = min(id) загруженного). Дельта знаковая, с учётом инверсии чата.

export const BalanceChangeSource = z.enum(['bot', 'telegram', 'tron']);
export type BalanceChangeSource = z.infer<typeof BalanceChangeSource>;

export const BalanceChangeRow = z.object({
  id: z.number().int(), // курсор пагинации
  occurredAt: z.string(), // ISO — момент фиксации у нас
  source: BalanceChangeSource,
  chatId: z.string(),
  title: z.string(),
  ccy: z.string(), // для бота — как отдал апстрим (UPPER), может не входить в CCY_CODES
  delta: MoneyStr, // знаковая
  actor: z.string().nullable(),
  externalTime: z.string().nullable(), // время бота (source='bot'), как отдал апстрим
});
export type BalanceChangeRow = z.infer<typeof BalanceChangeRow>;

export const BalanceChangesResponse = z.object({
  items: z.array(BalanceChangeRow),
  hasMore: z.boolean(),
});
export type BalanceChangesResponse = z.infer<typeof BalanceChangesResponse>;

export const BalanceChangesQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  before: z.coerce.number().int().positive().optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
});
export type BalanceChangesQuery = z.infer<typeof BalanceChangesQuery>;

// ─── Сверка расхождений (/balance-changes/unmatched) ─────────────────────────
//
// Эвристический матчинг «ноги ↔ ноги» по сумме USDT-эквивалента в окне
// ±2 часа от запрошенного [from, to] (см. matching.ts). Наружу отдаются
// только непарные строки из запрошенного окна крупнее minUsdt.

/** Непарное движение журнала — кандидат в расхождения. */
export const UnmatchedRow = z.object({
  id: z.number().int(),
  occurredAt: z.string(),
  source: BalanceChangeSource,
  chatId: z.string(),
  title: z.string(),
  ccy: z.string(),
  delta: MoneyStr,
  deltaUsdt: MoneyStr,
  actor: z.string().nullable(),
});
export type UnmatchedRow = z.infer<typeof UnmatchedRow>;

export const UnmatchedResponse = z.object({
  items: z.array(UnmatchedRow),
  /** Сколько пар «нога ↔ нога» удалось связать в окне (диагностика). */
  matchedPairs: z.number().int(),
  /** Строк пропущено из-за отсутствия курса валюты. */
  skipped: z.number().int(),
  /**
   * true — выборка строк журнала была обрезана предохранителем объёма
   * (MAX_ROWS в matching.ts): в окне их больше, чем сервер готов сверить за
   * раз. Взяты самые свежие строки, более старые из окна в сверке НЕ
   * участвовали — результат может быть неполным (пропущенные пары/непарные).
   */
  truncated: z.boolean(),
});
export type UnmatchedResponse = z.infer<typeof UnmatchedResponse>;

export const UnmatchedQuery = z
  .object({
    from: z.string().datetime(),
    to: z.string().datetime(),
    minUsdt: z.coerce.number().min(0).default(2000),
  })
  .refine((q) => new Date(q.from) <= new Date(q.to), {
    message: 'from must be <= to',
    path: ['from'],
  });
export type UnmatchedQuery = z.infer<typeof UnmatchedQuery>;
